import React, { useEffect, useState } from "react";
import {
  MessageBar,
  MessageBarType,
  Dropdown,
  IDropdownOption,
  Toggle,
} from "@fluentui/react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import TextInput from "@/ui-controls/TextInput";
import CustomerComboBox, {
  findCustomerName,
} from "@/ui-controls/CustomerComboBox";
import UserComboBox, {
  findUserEmail,
} from "@/ui-controls/UserComboBox";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  updateIntegrationConfiguration,
  listConfigurationProperties,
  createConfigurationProperty,
  updateConfigurationProperty,
  deleteConfigurationProperty,
} from "@/api/integrations";
import type { IntegrationConfigurationDto } from "@/api/integrations";
import ConfigurationProperties, {
  PropertyEntry,
} from "@/components/integrations/configurations/ConfigurationProperties";

interface Props {
  integrationId: number;
  integrationCode: string;
  configuration: IntegrationConfigurationDto;
  onSave?: () => void;
  onClose?: () => void;
}

const scopeOptions: IDropdownOption[] = [
  { key: 1, text: "Global" },
  { key: 2, text: "Customer" },
  { key: 3, text: "User" },
];

const authOptions: IDropdownOption[] = [
  { key: 1, text: "API Key" },
  { key: 2, text: "Username / Password" },
  { key: 3, text: "Username / Password / Company Code" },
];

const EditConfigurationForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [properties, setProperties] = useState<PropertyEntry[]>([]);
  const [originalProperties, setOriginalProperties] = useState<
    PropertyEntry[]
  >([]);
  const config = props.configuration;

  useEffect(() => {
    listConfigurationProperties(config.id)
      .then((data) => {
        const entries: PropertyEntry[] = (data || []).map((p) => ({
          id: p.id,
          propertyName: p.propertyName,
          propertyValue: p.propertyValue,
        }));
        setProperties(entries);
        setOriginalProperties(entries);
      })
      .catch(() => {});
  }, [config.id]);

  const formik = useFormik({
    initialValues: {
      scopeType: config.scopeType,
      customerId: config.customerId?.toString() ?? "",
      userId: config.userId?.toString() ?? "",
      authType: config.authType,
      apiKey: config.apiKey ?? "",
      username: config.username ?? "",
      password: config.password ?? "",
      companyCode: config.companyCode ?? "",
      configJson: config.configJson ?? "",
      configName: config.configName ?? "",
      effectiveFrom: config.effectiveFrom
        ? config.effectiveFrom.split("T")[0]
        : "",
      effectiveTo: config.effectiveTo ? config.effectiveTo.split("T")[0] : "",
      isActive: config.isActive,
      isTestMode: config.isTestMode ?? false,
    },
    validationSchema: Yup.object({
      effectiveFrom: Yup.string().required("Effective from is required"),
    }),
    onSubmit: async (values) => {
      setIsSaving(true);
      setErrorMessage("");
      try {
        await updateIntegrationConfiguration({
          id: config.id,
          integrationId: props.integrationId,
          scopeType: values.scopeType,
          customerId: values.customerId ? Number(values.customerId) : null,
          userId: values.userId ? Number(values.userId) : null,
          authType: values.authType,
          apiKey: values.apiKey || null,
          username: values.username || null,
          password: values.password || null,
          companyCode: values.companyCode || null,
          configJson: values.configJson || null,
          configName: values.configName || null,
          effectiveFrom: values.effectiveFrom,
          effectiveTo: values.effectiveTo || null,
          isActive: values.isActive,
          isTestMode: values.isTestMode || null,
        });

        // Delete removed properties
        const currentIds = new Set(
          properties.filter((p) => p.id).map((p) => p.id)
        );
        const deletedProps = originalProperties.filter(
          (p) => p.id && !currentIds.has(p.id)
        );
        for (const prop of deletedProps) {
          await deleteConfigurationProperty(prop.id!);
        }

        // Add new and update existing properties
        const validProps = properties.filter(
          (p) => p.propertyName.trim() && p.propertyValue.trim()
        );
        for (const prop of validProps) {
          if (prop.id) {
            const original = originalProperties.find(
              (o) => o.id === prop.id
            );
            if (
              original &&
              (original.propertyName !== prop.propertyName ||
                original.propertyValue !== prop.propertyValue)
            ) {
              await updateConfigurationProperty({
                id: prop.id,
                integrationConfigurationId: config.id,
                propertyName: prop.propertyName.trim(),
                propertyValue: prop.propertyValue,
              });
            }
          } else {
            await createConfigurationProperty({
              integrationConfigurationId: config.id,
              propertyName: prop.propertyName.trim(),
              propertyValue: prop.propertyValue,
            });
          }
        }

        props.onSave?.();
      } catch (error: any) {
        const data = error?.response?.data;
        setErrorMessage(data?.detail || "Failed to update configuration.");
      } finally {
        setIsSaving(false);
      }
    },
  });

  useEffect(() => {
    const code = props.integrationCode;
    if (!code) {
      formik.setFieldValue("configName", "");
      return;
    }

    const scope = formik.values.scopeType;
    const customerId = formik.values.customerId
      ? Number(formik.values.customerId)
      : 0;
    const userId = formik.values.userId ? Number(formik.values.userId) : 0;

    let name = "";
    if (scope === 1) {
      name = `PCS GLOBAL - ${code}`;
    } else if (scope === 2 && customerId) {
      const customerName = findCustomerName(customerId) ?? "";
      name = customerName ? `${customerName} - ${code}` : "";
    } else if (scope === 3 && customerId && userId) {
      const customerName = findCustomerName(customerId) ?? "";
      const userEmail = findUserEmail(customerId, userId) ?? "";
      name =
        customerName && userEmail
          ? `${customerName} - ${userEmail} - ${code}`
          : "";
    }

    if (name) {
      formik.setFieldValue("configName", name);
    } else if (!formik.values.configName && config.configName) {
      formik.setFieldValue("configName", config.configName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.integrationCode,
    formik.values.scopeType,
    formik.values.customerId,
    formik.values.userId,
  ]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <Dropdown
              label="Scope Type"
              options={scopeOptions}
              selectedKey={formik.values.scopeType}
              onChange={(_, option) =>
                formik.setFieldValue("scopeType", option?.key ?? 1)
              }
              disabled={isSaving}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <Dropdown
              label="Auth Type"
              options={authOptions}
              selectedKey={formik.values.authType}
              onChange={(_, option) =>
                formik.setFieldValue("authType", option?.key ?? 1)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Config Name"
              baseTypeProps={{
                readOnly: true,
                ...formik.getFieldProps("configName"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        {formik.values.scopeType >= 2 && (
          <Grid.Row>
            <Grid.Col sizeSm={6}>
              <CustomerComboBox
                selectedCustomerId={
                  formik.values.customerId
                    ? Number(formik.values.customerId)
                    : undefined
                }
                onChange={(id) =>
                  formik.setFieldValue("customerId", id ? String(id) : "")
                }
                disabled={isSaving}
              />
            </Grid.Col>
            {formik.values.scopeType >= 3 && (
              <Grid.Col sizeSm={6}>
                <UserComboBox
                  customerId={
                    formik.values.customerId
                      ? Number(formik.values.customerId)
                      : undefined
                  }
                  selectedUserId={
                    formik.values.userId
                      ? Number(formik.values.userId)
                      : undefined
                  }
                  onChange={(id) =>
                    formik.setFieldValue("userId", id ? String(id) : "")
                  }
                  disabled={isSaving}
                />
              </Grid.Col>
            )}
          </Grid.Row>
        )}
        {formik.values.authType === 1 && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <TextInput
                label="API Key"
                baseTypeProps={{
                  disabled: isSaving,
                  type: "password",
                  canRevealPassword: true,
                  ...formik.getFieldProps("apiKey"),
                }}
              />
            </Grid.Col>
          </Grid.Row>
        )}
        {(formik.values.authType === 2 || formik.values.authType === 3) && (
          <Grid.Row>
            <Grid.Col sizeSm={6}>
              <TextInput
                label="Username"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("username"),
                }}
              />
            </Grid.Col>
            <Grid.Col sizeSm={6}>
              <TextInput
                label="Password"
                baseTypeProps={{
                  disabled: isSaving,
                  type: "password",
                  canRevealPassword: true,
                  ...formik.getFieldProps("password"),
                }}
              />
            </Grid.Col>
          </Grid.Row>
        )}
        {formik.values.authType === 3 && (
          <Grid.Row>
            <Grid.Col sizeSm={6}>
              <TextInput
                label="Company Code"
                baseTypeProps={{
                  disabled: isSaving,
                  ...formik.getFieldProps("companyCode"),
                }}
              />
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <TextInput
              label="Config JSON"
              baseTypeProps={{
                disabled: isSaving,
                multiline: true,
                rows: 4,
                ...formik.getFieldProps("configJson"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <ConfigurationProperties
              properties={properties}
              onChange={setProperties}
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective From"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveFrom"),
              }}
              hasError={
                formik.errors.effectiveFrom && formik.touched.effectiveFrom
              }
              errorMessage={formik.errors.effectiveFrom}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <TextInput
              label="Effective To"
              baseTypeProps={{
                disabled: isSaving,
                type: "date",
                ...formik.getFieldProps("effectiveTo"),
              }}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Active"
              checked={formik.values.isActive}
              onChange={(_, checked) =>
                formik.setFieldValue("isActive", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
          <Grid.Col sizeSm={6}>
            <Toggle
              label="Test Mode"
              checked={formik.values.isTestMode}
              onChange={(_, checked) =>
                formik.setFieldValue("isTestMode", checked ?? false)
              }
              disabled={isSaving}
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={() => props.onClose?.()}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

export default EditConfigurationForm;
